// React
import { memo } from "react";

// Servisni sloj
import { formatUnitPrice } from "services/api/pricing/consts";

// UI
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { TableSearch } from "ui/components/Table/TableSearch";

// Tipovi i konstante
import { SCHEME_IMPACT_COLUMNS } from "./consts";
import { PricingSchemeImpactCardProps } from "./types";

/**
 * Učinak sheme prije primjene — koliko je klijenata zahvaćeno i koliki je mjesečni iznos.
 *
 * Ključno: zbroj obuhvaća samo ono što je stvarno izračunato. Ključevi kojima se količina ne
 * zna navode se izrijekom, jer bi tiho izostavljanje dalo zbroj koji izgleda potpun a nije.
 */
const PricingSchemeImpactCard: React.FC<PricingSchemeImpactCardProps> = (
  props: PricingSchemeImpactCardProps
) => {
  const { scheme, impact, onClose } = props;

  return (
    <Modal show={scheme !== null} onHide={onClose} size="large" position="centered">
      <ModalHeader
        title={`Učinak grupe ${scheme?.name ?? ""}`}
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />

      <ModalBody fixedFooterSpacing={false}>
        {impact === null ? (
          <div className="p-4">
            <p className="text-sm text-slate-500">Učitavanje…</p>
          </div>
        ) : (
          <div className="flex flex-col gap-5 p-4">
            <div className="flex flex-wrap gap-8">
              <div>
                <p className="text-xs text-slate-500">Klijenata u grupi</p>
                <p className="text-2xl font-bold text-slate-700">{impact.tenantCount}</p>
              </div>

              <div>
                <p className="text-xs text-slate-500">Izračunati mjesečni učinak</p>
                <p className="text-2xl font-bold text-slate-700">
                  {formatUnitPrice.format(impact.monthlyEffect)}
                </p>
              </div>
            </div>

            {impact.unmeasured.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-800 mb-1">
                  Zbroj ne obuhvaća sve stavke grupe
                </p>
                <p className="text-sm text-amber-800">
                  Za ove ključeve se mjesečna količina ne zna, pa nisu ušli u iznos:
                </p>
                <ul className="list-disc pl-5 text-sm text-amber-800 mt-1">
                  {impact.unmeasured.map((code) => (
                    <li key={code} className="font-mono text-xs">
                      {code}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-amber-700 mt-2">
                  Cijene po vozilu postanu izračunljive kad pretplata počne nositi kombinaciju
                  paketa; potrošnja mjerenih stavki se zasad nigdje ne mjeri.
                </p>
              </div>
            )}

            {impact.tenants.length === 0 ? (
              <p className="text-sm text-slate-500">
                Nijedan klijent nije u grupi „{scheme?.name}".
              </p>
            ) : (
              <TableSearch
                columns={SCHEME_IMPACT_COLUMNS}
                data={impact.tenants}
                selectableRows={false}
                disablePagination
              />
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button variant="outlined" children="Zatvori" onClick={onClose} />
      </ModalFooter>
    </Modal>
  );
};

export default memo(PricingSchemeImpactCard);
